<template>
  <div class="p-3 bg-light rounded">
    <div v-if="loading" class="text-center py-2">
      <b-spinner small variant="primary" />
      <p class="text-muted small mt-1">Loading status...</p>
    </div>
    <template v-else-if="session">
      <!-- OTA Steps -->
      <div v-if="updateType === 'ota'">
        <div class="small text-muted mb-2">
          <strong>Target:</strong> {{ session.target_version }}<br />
          <strong>Current:</strong> {{ session.current_fw_version }}
        </div>
        <div class="d-flex flex-column" style="gap:6px;">
          <div class="d-flex align-items-center">
            <i :class="steps.sent ? 'fas fa-check-circle text-success' : 'fas fa-circle-notch fa-spin text-primary'" class="mr-2"></i>
            <span>1. Sending update command</span>
          </div>
          <div class="d-flex align-items-center">
            <i :class="steps.downloadCompleted ? 'fas fa-check-circle text-success' : (steps.downloading ? 'fas fa-circle-notch fa-spin text-primary' : 'far fa-circle text-muted')" class="mr-2"></i>
            <span>2. Downloading firmware</span>
          </div>
          <div v-if="session.has_wifi" class="d-flex align-items-center">
            <i :class="steps.wifiCompleted ? 'fas fa-check-circle text-success' : (steps.sendingWifi ? 'fas fa-circle-notch fa-spin text-primary' : 'far fa-circle text-muted')" class="mr-2"></i>
            <span>3. Sending WiFi firmware</span>
          </div>
          <div class="d-flex align-items-center">
            <i :class="steps.completed ? 'fas fa-check-circle text-success' : (steps.rebooting ? 'fas fa-circle-notch fa-spin text-primary' : 'far fa-circle text-muted')" class="mr-2"></i>
            <span>{{ session.has_wifi ? '4' : '3' }}. Verifying new version</span>
          </div>
        </div>
      </div>

      <!-- Provision Steps (simplified) -->
      <div v-else-if="updateType === 'provision'">
        <div class="small text-muted mb-2">
          <strong>Files:</strong> {{ session.completed_files || 0 }} / {{ session.total_files || 0 }}
        </div>
        <b-progress :max="session.total_files || 1" height="6px" class="mb-2">
          <b-progress-bar :value="session.completed_files || 0" variant="success" />
          <b-progress-bar :value="session.downloading_files || 0" variant="primary" animated />
        </b-progress>
        <div v-if="session.files && session.files.length">
          <div v-for="file in session.files" :key="file.filename" class="d-flex align-items-center small">
            <i :class="file.status === 'completed' ? 'fas fa-check-circle text-success' : (file.status === 'downloading' ? 'fas fa-circle-notch fa-spin text-primary' : 'far fa-circle text-muted')" class="mr-2"></i>
            <span>{{ file.filename }}</span>
          </div>
        </div>
      </div>
    </template>
    <div v-else class="text-muted small text-center py-2">
      No active update session found.
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeviceProgressPanel',
  props: {
    deviceId: { type: String, required: true },
    entity: { type: String, required: true },
    updateType: { type: String, required: true }, // 'ota' or 'provision'
    status: { type: String, required: true },     // schedule entry status
  },
  data() {
    return {
      loading: false,
      session: null,
      pollTimer: null,
      steps: {
        sent: false,
        downloading: false,
        downloadCompleted: false,
        rebooting: false,
        rebootCompleted: false,
        sendingWifi: false,
        wifiCompleted: false,
        completed: false,
      },
      downloadingStartTime: null,
    };
  },
  watch: {
    status: {
      immediate: true,
      handler(newStatus) {
        if (newStatus === 'in_progress') {
          this.startPolling();
        } else {
          this.stopPolling();
          this.session = null;
        }
      },
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    startPolling() {
      this.stopPolling();
      this.poll();
      this.pollTimer = setInterval(() => this.poll(), 3000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async poll() {
      if (!this.deviceId || !this.entity) return;
      this.loading = true;
      try {
        if (this.updateType === 'ota') {
          const resp = await this.$http.get(
            `/tenant/${this.entity}/firmware/${this.deviceId}/ota/status`
          );
          this.applyOtaState(resp.data);
        } else {
          const resp = await this.$http.get(
            `/tenant/${this.entity}/provision/${this.deviceId}/status`
          );
          this.session = resp.data;
          if (resp.data.status !== 'in_progress') {
            this.stopPolling();
          }
        }
      } catch (err) {
        console.error('Poll error', err);
        this.stopPolling();
      } finally {
        this.loading = false;
      }
    },
    applyOtaState(data) {
      if (!data || data.status === 'no_active_ota') {
        this.session = null;
        this.stopPolling();
        return;
      }

      this.session = {
        ...data,
        fw_version_after: data.fw_version_after || data.current_fw_version,
      };

      if (data.status === 'completed') {
        this.steps = {
          sent: true,
          downloading: false,
          downloadCompleted: true,
          rebooting: false,
          rebootCompleted: true,
          sendingWifi: false,
          wifiCompleted: !!data.has_wifi,
          completed: true,
        };
        this.stopPolling();
      } else if (data.status === 'failed' || data.status === 'cancelled') {
        this.steps = { ...this.steps, sent: true };
        this.stopPolling();
      } else if (data.status === 'in_progress') {
        this.steps.sent = true;

        if (data.has_wifi && data.wifi_sent) {
          this.steps.downloading = false;
          this.steps.downloadCompleted = true;
        }

        if (!this.steps.downloading && !this.steps.downloadCompleted) {
          this.steps.downloading = true;
          this.downloadingStartTime = this.downloadingStartTime || Date.now();
        }

        if (data.has_wifi && !data.wifi_sent && !this.steps.wifiCompleted) {
          this.steps.sendingWifi = true;
        }
        if (data.has_wifi && data.wifi_sent) {
          this.steps.sendingWifi = false;
          this.steps.wifiCompleted = true;
        }

        if (
          this.steps.downloading &&
          !this.steps.downloadCompleted &&
          !this.steps.rebooting &&
          this.downloadingStartTime &&
          (Date.now() - this.downloadingStartTime) > 5000 &&
          data.current_fw_version === data.fw_version_before
        ) {
          this.steps.downloading = false;
          this.steps.downloadCompleted = true;
          this.steps.rebooting = true;
        }
      }
    },
  },
};
</script>